import { Fragment, useMemo } from "react";
import { TableBody } from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router";
import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead.tsx";
import {
	HoveredStyledTableRow,
	StyledTableCell,
	StyledTableWrapper
} from "components/Common/Table/StyledTable.styles.ts";
import { LastUpdatedText, StatusText, UnavailableDataPlaceholderText } from "constants/text.ts";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { models } from "types/api/viewModels.ts";
import { getAssignedToLocation } from "features/LeadGen/utils/leadAssignmentLocation.ts";
import { getBrandDisplayName } from "utils/order.ts";
import { useAssignedToPersonNameLookup } from "../../hooks";
import {
	formatLeadDate,
	getAssignedToPersonName,
	getLeadDateSortValue,
	getLeadFullName,
	getLeadStatusDisplay,
	getLeadStatusLabel
} from "features/LeadGen/pages/LeadAssignments/utils/LeadAssignmentsPage.utils.ts";
import { getRelativeTimeString } from "./utils/LeadAssignmentsTable.utils.ts";

interface LeadAssignmentsTableProps {
	leads: models["LeadAssignmentViewModel"][];
}

type LeadAssignmentsSortableColumn =
	| "updatedAt"
	| "createdAt"
	| "leadNumber"
	| "leadName"
	| "assignedTo"
	| "assignedToPerson"
	| "brand"
	| "status";

interface LeadAssignmentsTableRowModel {
	lead: models["LeadAssignmentViewModel"];
	updatedAt: number;
	createdAt: number;
	createdAtDisplay: string;
	leadNumber: string;
	leadName: string;
	assignedTo: string;
	assignedToPerson: string;
	brand: string;
	status: string;
	statusDisplay: string;
}

const buildLeadAssignmentsTableRowModel = (
	lead: models["LeadAssignmentViewModel"],
	assignedToPersonNameLookup: Record<string, string>
): LeadAssignmentsTableRowModel => {
	const statusLabel = getLeadStatusLabel(lead.status);

	return {
		lead,
		updatedAt: getLeadDateSortValue(lead.updatedAt),
		createdAt: getLeadDateSortValue(lead.createdAt),
		createdAtDisplay: formatLeadDate(lead.createdAt),
		leadNumber: lead.leadId ?? UnavailableDataPlaceholderText,
		leadName: getLeadFullName(lead) || UnavailableDataPlaceholderText,
		assignedTo: getAssignedToLocation(lead) ?? UnavailableDataPlaceholderText,
		assignedToPerson: getAssignedToPersonName(lead, assignedToPersonNameLookup),
		brand: getBrandDisplayName(lead.lead?.brand) || UnavailableDataPlaceholderText,
		status: lead.followUpDate
			? `${statusLabel} ${lead.followUpDate}`
			: statusLabel || UnavailableDataPlaceholderText,
		statusDisplay: getLeadStatusDisplay(lead)
	};
};

const LeadAssignmentsTableRow = ({
	lead,
	createdAtDisplay,
	leadNumber,
	leadName,
	assignedTo,
	assignedToPerson,
	brand,
	statusDisplay
}: LeadAssignmentsTableRowModel) => {
	const leadAssignmentsTableCells = [
		{
			testId: "lead-assignment-table-lastUpdated",
			props: { fixedWidth: "100px" },
			content: getRelativeTimeString(lead.updatedAt)
		},
		{
			testId: "lead-assignment-table-createdDate",
			content: createdAtDisplay
		},
		{
			testId: "lead-assignment-table-leadNumber",
			content: leadNumber
		},
		{
			testId: "lead-assignment-table-leadName",
			content: leadName
		},
		{
			testId: "lead-assignment-table-assignedTo",
			content: assignedTo
		},
		{
			testId: "lead-assignment-table-assignedPerson",
			content: assignedToPerson
		},
		{
			testId: "lead-assignment-table-brand",
			content: brand
		},
		{
			testId: "lead-assignment-table-status",
			content: statusDisplay
		},
		{
			testId: "lead-assignment-arrow",
			props: { fixedWidth: "36px" },
			content: <ChevronRight />
		}
	];

	return (
		<Fragment>
			{leadAssignmentsTableCells.map(({ testId, props, content }) => (
				<StyledTableCell
					key={testId}
					data-testid={testId}
					{...props}
				>
					{content}
				</StyledTableCell>
			))}
		</Fragment>
	);
};

export const LeadAssignmentsTable = ({ leads }: LeadAssignmentsTableProps) => {
	const navigate = useNavigate();
	const assignedToPersonNameLookup = useAssignedToPersonNameLookup();
	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort<LeadAssignmentsSortableColumn>(
		"updatedAt",
		"desc"
	);
	const sortableLeads = useMemo(
		() => leads.map((lead) => buildLeadAssignmentsTableRowModel(lead, assignedToPersonNameLookup)),
		[assignedToPersonNameLookup, leads]
	);
	const sortedLeads = sortableLeads.slice().sort(getComparator(order, orderBy));

	const leadAssignmentsTableCells = useMemo(
		() =>
			[
				{ id: "updatedAt", label: LastUpdatedText, sortable: true },
				{ id: "createdAt", label: "Created Date", sortable: true },
				{ id: "leadNumber", label: "Lead #", sortable: true },
				{ id: "leadName", label: "Lead Name", sortable: true },
				{ id: "assignedTo", label: "Assigned To Location", sortable: true },
				{ id: "assignedToPerson", label: "Assigned Person", sortable: true },
				{ id: "brand", label: "Brand", sortable: true },
				{ id: "status", label: StatusText, sortable: true },
				{ id: "open", label: "", sortable: false }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false,
				sortable: cell.sortable
			})) satisfies HeadCell[],
		[]
	);

	return (
		<StyledTableWrapper
			data-testid="new-requests-table"
			aria-label="New Requests Table"
		>
			<SortableTableHead
				orderBy={orderBy}
				order={order}
				onRequestSort={handleRequestSort}
				headCells={leadAssignmentsTableCells}
			/>
			<TableBody>
				{sortedLeads.map((leadRow) => {
					const leadAssignmentId = leadRow.lead.leadAssignmentId ?? leadRow.lead.leadId ?? leadRow.leadNumber;

					return (
						<HoveredStyledTableRow
							key={leadAssignmentId}
							data-testid={`lead-${leadAssignmentId}`}
							onClick={() => {
								if (leadRow.lead.leadAssignmentId) {
									navigate(`/lead-assignments/lead/${leadRow.lead.leadAssignmentId}`);
								}
							}}
						>
							<LeadAssignmentsTableRow {...leadRow} />
						</HoveredStyledTableRow>
					);
				})}
			</TableBody>
		</StyledTableWrapper>
	);
};
